import { FC } from "react";
import { useData } from "data/data-provider";
import HeroSearchForm from "designs/Common/HeroSearchForm";
import DynamicMobileSearch from "../DynamicMobileSearch/DynamicMobileSearch";
import { isUsableUrl } from "utils/isUsableUrl";

export interface SectionHeroProps {
  className?: string;
}

/**
 * Design A landing hero. Background image and copy come from the FMS-managed
 * `banner` Section row (see Backend website/models.Section, section_type =
 * "banner"). Renders a full-bleed image with the heading + description
 * overlaid above the search form.
 *
 * NB: Every read from `banner` is guarded — the data-provider populates this
 * asynchronously, so during the first render `banner` is undefined. Without
 * guards `\`url(${banner?.image_url})\`` produces the literal string
 * `url(undefined)` (which 404s and breaks the hero), and dangerouslySetInnerHTML
 * receives `undefined` (which React warns about). The image guard uses
 * `isUsableUrl` so the literal strings `"undefined"` / `"null"` — which can
 * sneak in through upstream stringification — fall through to the themed
 * fallback instead of producing `url(undefined)` (ONENOW-457).
 */
const SectionHero: FC<SectionHeroProps> = ({ className = "" }) => {
  const { banner } = useData();

  const desktopBg = isUsableUrl(banner?.image_url) ? banner.image_url : "";
  const mobileBgCandidate = banner?.mobile_image_url || banner?.image_url;
  const mobileBg = isUsableUrl(mobileBgCandidate) ? mobileBgCandidate : "";

  // Themed gradient fallback — uses the active palette so the hero looks
  // intentional even before the image arrives (or if the upload is missing).
  const themedFallback =
    "linear-gradient(135deg, rgba(var(--c-button-color)) 0%, rgba(var(--c-primary-300)) 100%)";

  const headingHtml = banner?.heading || "";
  const descriptionHtml = banner?.description || "";

  // Banner text alignment is chosen in the FMS (Section.alignment). Design A's
  // long-standing default is centered, so anything other than an explicit
  // "left"/"right" (unset, null, or an unknown value) keeps the original
  // centered layout exactly.
  //   "center" → block centered            (default — unchanged)
  //   "left"   → block + text on the left
  //   "right"  → block + text on the right
  const alignment: "left" | "center" | "right" =
    banner?.alignment === "left" || banner?.alignment === "right"
      ? banner.alignment
      : "center";

  // Flat string maps (not template strings) so Tailwind's JIT statically
  // detects every class — `items-${alignment}` would silently miss. These
  // reuse Design A's own hero classes; the "center" entries reproduce the
  // pre-existing `items-center mx-auto` / `text-center` markup byte-for-byte.
  const blockAlignment: Record<typeof alignment, string> = {
    left: "items-start mr-auto",
    center: "items-center mx-auto",
    right: "items-end ml-auto",
  };
  const textAlignment: Record<typeof alignment, string> = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };

  return (
    <>
      {/* Desktop banner — hidden on mobile */}
      <div
        className={`nc-SectionHero3 hidden lg:flex mx-0 min-h-[80vh] items-center justify-center ${className}`}
        data-nc-id="SectionHero3"
        style={{
          backgroundImage: desktopBg ? `url(${desktopBg})` : themedFallback,
          // Always cover so the hero image scales responsively at every
          // screen size. Fixed px width/height used to pin the image here and
          // break responsiveness; those inputs are now hidden in the FMS.
          backgroundSize: banner?.position ?? "cover",
          // Banner image position is its own control (Section.image_alignment),
          // independent of the heading/description/search-bar text alignment.
          backgroundPosition: banner?.image_alignment ?? "center",
          backgroundRepeat: "no-repeat",
          position: "relative",
        }}
      >
        {/* Subtle dark scrim — keeps headline legible on bright photos and
            also gives the themed fallback some depth. */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/45"
        />
        <div
          className={`inset-x-0 flex flex-col space-y-3 lg:space-y-3 xl:space-y-5 relative ${textAlignment[alignment]} ${blockAlignment[alignment]}`}
          style={{ zIndex: 1 }}
        >
          {headingHtml && (
            <p
              className={`font-bold text-2xl ${textAlignment[alignment]} max-w-7xl text-banner-heading-color bg-opacity-80 px-1 lg:bg-none lg:bg-opacity-0 lg:mx-0 mx-4 leading-none`}
              dangerouslySetInnerHTML={{ __html: headingHtml }}
            />
          )}
          {descriptionHtml && (
            <p
              className={`max-w-6xl text-2xl ${textAlignment[alignment]} text-banner-description-color px-2 md:px-6 rounded-full lg:mx-0 mx-4 font-medium`}
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          )}

          <div
            className="z-10 mb-12 lg:mb-0 w-full"
            style={{ marginTop: "0px" }}
          >
            <HeroSearchForm />
          </div>
        </div>
      </div>
      {/* Mobile banner — hidden on desktop, falls back to desktop image if no mobile image set */}
      <div
        className={`nc-SectionHero3 flex lg:hidden mx-0 min-h-[100vh] items-center justify-center ${className}`}
        data-nc-id="SectionHero3"
        style={{
          backgroundImage: mobileBg ? `url(${mobileBg})` : themedFallback,
          backgroundSize: "cover",
          backgroundPosition: banner?.image_alignment ?? "center",
          backgroundRepeat: "no-repeat",
          position: "relative",
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/45"
        />
        <div
          className={`inset-x-0 flex flex-col space-y-3 lg:space-y-3 xl:space-y-5 relative ${textAlignment[alignment]} ${blockAlignment[alignment]}`}
          style={{ zIndex: 1 }}
        >
          {headingHtml && (
            <p
              className={`font-bold text-2xl ${textAlignment[alignment]} max-w-7xl text-banner-heading-color bg-opacity-80 px-1 lg:bg-none lg:bg-opacity-0 lg:mx-0 mx-4 leading-none`}
              dangerouslySetInnerHTML={{ __html: headingHtml }}
            />
          )}
          {descriptionHtml && (
            <p
              className={`max-w-6xl text-2xl ${textAlignment[alignment]} text-banner-description-color px-2 md:px-6 rounded-full lg:mx-0 mx-4 font-medium`}
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          )}

          <div className="!mx-auto px-3 w-full">
            <DynamicMobileSearch />
          </div>
        </div>
      </div>
    </>
  );
};

export default SectionHero;
